import React, { useState, useCallback, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { isNil, head, take } from 'ramda'
import { isFalsy, isEmptyString, isEmptyArray } from 'ramda-adjunct'
import { useIntl } from 'gatsby-plugin-intl'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import { breakpoint } from 'src/theme/grid'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import {
  getLastUsedStorageKey,
  readLastUsedArray,
  writeLastUsedArray
} from './lastUsedStorage'
import useClickOutside from 'src/hooks/useClickOutside'
import ReactDOM from 'react-dom'
import CircularProgress from '@material-ui/core/CircularProgress'
import PlaceSuggestion from 'src/components/PlaceSuggestion'
import {
  SelectInput,
  FilterDropdown,
  SearchInput,
  SearchInputWrapper,
  SelectOptionsWrapper,
  SelectRow,
  OptionsSectionTitle
} from './components/atoms'

const PlaceBox = ({
  className,
  id,
  label,
  locationName,
  locationValue,
  placeholder,
  onChange,
  locationTypes,
  dictionary,
  fetchLocations,
  required,
  locationPlaceholder,
  helperText,
  pristine,
  validate,
  searchHintText,
  isLoading
}) => {
  const intl = useIntl()
  const currentUserId = useSelector(selectCurrentUserId)
  const [filterOpened, setFilterOpened] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [lastUsedOptions, setLastUsedOptions] = useState([])
  const [selectedOptions, setSelectedOptions] = useState([])
  const [inputPristine, _setPristine] = useState(pristine)
  const [{ valid, error }, _validate] = useState({ valid: true, errors: [] })
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const [showSuggestionButton, setShowSuggestionButton] = useState(false)
  const toggleDropdown = () => setFilterOpened(!filterOpened)
  const componentRef = useClickOutside(() => setFilterOpened(false))
  const emptyText = isEmptyString(searchText) || searchText.length < 2
  const isMultiple = () => Array.isArray(locationValue)
  const lastUsedLocalStorageKey = getLastUsedStorageKey(
    dictionary.name,
    locationTypes,
    currentUserId
  )
  const normalizeLocationOption = option => {
    const plainOption =
      option && typeof option.toJS === 'function' ? option.toJS() : option

    if (isNil(plainOption)) return null

    const value = isNil(plainOption.value) ? plainOption.id : plainOption.value
    const optionLabel = isNil(plainOption.label)
      ? plainOption.name
      : plainOption.label

    return isNil(value) || isFalsy(optionLabel)
      ? null
      : { label: optionLabel, value }
  }
  const getLocationValue = value => {
    const plainValue =
      value && typeof value.toJS === 'function' ? value.toJS() : value

    if (isNil(plainValue) || typeof plainValue !== 'object') {
      return plainValue
    }

    return isNil(plainValue.value) ? plainValue.id : plainValue.value
  }
  const getSelectedLocationValues = () =>
    (isMultiple() ? locationValue : [locationValue])
      .map(getLocationValue)
      .filter(value => !isFalsy(value))
  const areLocationValuesEqual = (firstValue, secondValue) =>
    String(firstValue) === String(secondValue)
  const includesLocationValue = (values, value) =>
    values.some(currentValue => areLocationValuesEqual(currentValue, value))
  const findOptionByValue = (options, value) =>
    head(options.filter(option => areLocationValuesEqual(option.value, value)))
  const getUniqueLocationOptions = options =>
    options.reduce(
      (uniqueOptions, option) =>
        uniqueOptions.some(uniqueOption =>
          areLocationValuesEqual(uniqueOption.value, option.value)
        )
          ? uniqueOptions
          : uniqueOptions.concat(option),
      []
    )
  const rememberSelectedOptions = options => {
    const normalizedOptions = options
      .map(normalizeLocationOption)
      .filter(Boolean)

    if (isEmptyArray(normalizedOptions)) return

    setSelectedOptions(currentOptions => {
      const otherOptions = currentOptions.filter(currentOption =>
        normalizedOptions.every(
          option => !areLocationValuesEqual(option.value, currentOption.value)
        )
      )
      return otherOptions.concat(normalizedOptions)
    })
  }
  const availableLastUsedOptions = lastUsedOptions

  const debouncedSearch = useCallback(
    debounce(text => {
      try {
        fetchLocations({
          filter: {
            type: locationTypes,
            name: text
          },
          dictionaryName: dictionary.name,
          limit: dictionary.limit,
          page: dictionary.page
        })
      } catch (fetchError) {
        console.error('Error fetching locations:', fetchError)
      }
    }, 200),
    []
  )

  useEffect(() => {
    //search from 2 characters and for empty string to get full list after clear
    if (emptyText) {
      setShowSuggestionButton(false)
      return
    }
    debouncedSearch(searchText)
  }, [searchText])

  useEffect(() => {
    const selectedLocationValues = getSelectedLocationValues()

    if (!isEmptyArray(selectedLocationValues)) {
      rememberSelectedOptions(
        dictionary.values.filter(option =>
          includesLocationValue(selectedLocationValues, option.value)
        )
      )
    }
  }, [dictionary.values, locationValue, lastUsedOptions])

  useEffect(() => {
    const normalizedOptions = getUniqueLocationOptions(
      readLastUsedArray(lastUsedLocalStorageKey)
        .map(normalizeLocationOption)
        .filter(Boolean)
    )
    writeLastUsedArray(lastUsedLocalStorageKey, normalizedOptions)

    setLastUsedOptions(normalizedOptions)

    const savedOptionValues = normalizedOptions.map(option => option.value)
    if (!isEmptyArray(savedOptionValues)) {
      fetchLocations({
        filter: {
          id: savedOptionValues
        },
        dictionaryName: dictionary.name,
        limit: dictionary.limit,
        page: dictionary.page,
        append: true
      })
    }
  }, [lastUsedLocalStorageKey])

  const saveLastUsedElement = option => {
    const normalizedOption = normalizeLocationOption(option)
    if (isNil(normalizedOption)) return

    const otherOptions = lastUsedOptions.filter(
      savedOption =>
        !areLocationValuesEqual(savedOption.value, normalizedOption.value)
    )
    //only last 5 options is shown
    const optionsToSave = [normalizedOption].concat(take(4)(otherOptions))
    writeLastUsedArray(lastUsedLocalStorageKey, optionsToSave)
    setLastUsedOptions(optionsToSave)
  }

  //append options when location changes
  useEffect(() => {
    const selectedLocationValues = getSelectedLocationValues()
    if (isEmptyArray(selectedLocationValues)) return

    fetchLocations({
      filter: {
        id: selectedLocationValues
      },
      dictionaryName: dictionary.name,
      limit: dictionary.limit,
      page: dictionary.page,
      append: true
    })
  }, [locationValue])

  useEffect(() => {
    if (!inputPristine || !pristine) {
      validate(locationName, locationValue, v => {
        _validate(() => ({ valid: v.valid, error: v.errors.join(' ') }))
      })
    }
  }, [locationValue, inputPristine, pristine])

  const handleClose = () => {
    _setPristine(false)
    setFilterOpened(false)
  }

  const isLocationSelected = locationId =>
    includesLocationValue(getSelectedLocationValues(), locationId)

  const generateFilterName = () => {
    const selected = head(getSelectedLocationValues())
    if (isFalsy(selected)) {
      return locationPlaceholder
    }

    const selectedOption =
      findOptionByValue(dictionary.values, selected) ||
      findOptionByValue(selectedOptions, selected) ||
      findOptionByValue(lastUsedOptions, selected)
    return !isFalsy(selectedOption) ? selectedOption.label : locationPlaceholder
  }

  const handleLocationChange = (optionValue, isThisValueSelected) => {
    const singleValue = () => (isThisValueSelected ? null : optionValue)
    const multipleValue = () =>
      isThisValueSelected
        ? locationValue.filter(
            value =>
              !areLocationValuesEqual(getLocationValue(value), optionValue)
          )
        : [...locationValue, optionValue]
    if (!isThisValueSelected) {
      rememberSelectedOptions([
        findOptionByValue(dictionary.values, optionValue) ||
          findOptionByValue(lastUsedOptions, optionValue)
      ])
    }
    onChange(locationName, isMultiple() ? multipleValue() : singleValue())
    !isThisValueSelected && handleClose()
  }

  const handleClear = () => {
    onChange(locationName, isMultiple() ? [] : null)
    setSearchText('')
  }

  return (
    <div className={className} ref={componentRef}>
      <Label required={required}>{label}</Label>
      <SelectInput
        onClick={toggleDropdown}
        opened={filterOpened}
        hasValue={
          isMultiple() ? locationValue.length > 0 : !isNil(locationValue)
        }
        id={`filter-${id}`}
        selectedNumber={isMultiple() ? locationValue.length : null}
        onClear={handleClear}
      >
        {generateFilterName()}
      </SelectInput>
      {filterOpened && (
        <FilterDropdownStyled
          filterName={label}
          onClose={() => handleClose()}
          onClear={handleClear}
          clearButtonText={intl.formatMessage({ id: 'offersFilters.clear' })}
          confirmButtonText={intl.formatMessage({
            id: 'offersFilters.confirm'
          })}
        >
          <SearchInputWrapper>
            <SearchInput
              name={locationName}
              id={`input-${id}`}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder={placeholder}
            />
          </SearchInputWrapper>
          <SelectOptionsWrapperStyled>
            {emptyText && isEmptyArray(availableLastUsedOptions) && (
              <SearchHint>{searchHintText}</SearchHint>
            )}
            {emptyText && !isEmptyArray(availableLastUsedOptions) && (
              <>
                <OptionsSectionTitle>
                  {intl.formatMessage({ id: 'offersFilters.lastOptions' })}
                </OptionsSectionTitle>
                {availableLastUsedOptions.map(option => {
                  const isSelected = isLocationSelected(option.value)
                  return (
                    <SelectRow
                      key={option.value}
                      selected={isSelected}
                      name={option.label}
                      onClick={() => {
                        handleLocationChange(option.value, isSelected)
                      }}
                    />
                  )
                })}
              </>
            )}
            {!emptyText &&
              dictionary.values.map(option => {
                const isSelected = isLocationSelected(option.value)
                return (
                  <SelectRow
                    key={option.value}
                    selected={isSelected}
                    name={option.label}
                    onClick={() => {
                      handleLocationChange(option.value, isSelected)
                      !isSelected && saveLastUsedElement(option)
                    }}
                  />
                )
              })}
            {!emptyText &&
              isEmptyArray(dictionary.values) &&
              (isLoading ? (
                <>
                  <LoaderContainer>
                    <Loader />
                  </LoaderContainer>
                  {(() => {
                    setTimeout(() => {
                      setShowSuggestionButton(true)
                    }, 0)
                    return null
                  })()}
                </>
              ) : (
                (!dictionary ||
                  !dictionary.values ||
                  isEmptyArray(dictionary.values)) &&
                !isLoading &&
                showSuggestionButton && (
                  <>
                    <SearchHint>
                      {intl.formatMessage({
                        id: 'offersFilters.placeNotFound'
                      })}
                    </SearchHint>
                    <SuggestButton onClick={() => setShowSuggestionModal(true)}>
                      {intl.formatMessage({ id: 'common.addSuggestion' })}
                    </SuggestButton>
                  </>
                )
              ))}
          </SelectOptionsWrapperStyled>
        </FilterDropdownStyled>
      )}
      <HelperText valid={valid}>{valid ? helperText : error}</HelperText>

      {showSuggestionModal &&
        ReactDOM.createPortal(
          <PlaceSuggestion
            onClose={() => setShowSuggestionModal(false)}
            placeName={searchText}
            locationType={locationTypes}
          />,
          document.body
        )}
    </div>
  )
}

PlaceBox.defaultProps = {
  value: '',
  pristine: true,
  validate: () => true
}

export default styled(PlaceBox)`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      div {
        opacity: 0.7;
      }
    `}
  ${FilterDropdown} {
    ${breakpoint.m`
      width: 320px;
      height: 400px;
    `}
  }
`
const SelectOptionsWrapperStyled = styled(SelectOptionsWrapper)`
  height: calc(100% - 40px);
`

const Label = styled.span`
  display: flex;
  width: 100%;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.3rem;
  margin-left: 0.2rem;
  ${({ required }) =>
    required &&
    `
    &:after {
      content: '*';
      color: red;
      font-size: 0.75rem;
    }
  `}
`

const FilterDropdownStyled = styled(FilterDropdown)`
  ${breakpoint.m`
    top: 70px;
  `}
`

const HelperText = styled.span`
  display: inline-block;
  width: 100%;
  margin: 8px 6px;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1em;
  color: ${({ theme, valid }) =>
    valid ? theme.colors.ghost.hex() : theme.colors.error.hex()};
`

const SearchHint = styled.span`
  display: flex;
  padding: 0.8rem;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.71;
`

const SuggestButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem;
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.71;
  border: none;
  cursor: pointer;
  margin: 0.5rem auto;
  width: fit-content;
`

const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  color: ${({ theme }) => theme.colors.apple.hex()} !important;
  transform-origin: center center;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  width: 100%;
  min-height: 100px;
  background: transparent;
  position: static;
  box-sizing: border-box;
  overflow: hidden;
`
